import { ReactElement } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import type { AbstractAgent } from '@copilotkit/react-core/v2'
import { CHAT_LABELS } from '../utils'
import ApprovalModeSelect from './ApprovalModeSelect'
import ChatPanel from './ChatPanel'
import EmptyChatHints from './EmptyChatHints'

interface Props {
  projectId: string
  threadId: string
  agent: AbstractAgent
  /** The agent registered for this thread is there (see AIChatWidgetContent). */
  panelReady: boolean
  autoApprove: boolean
  onAutoApproveChange: (checked: boolean) => void
  onSelectThread: (threadId: string, model: string | null | undefined) => void
}

/**
 * The chat of one thread, with a footer under the message field: how its
 * changes are approved, and the disclaimer.
 */
const ChatBody = ({
  projectId,
  threadId,
  agent,
  panelReady,
  autoApprove,
  onAutoApproveChange,
  onSelectThread,
}: Props): ReactElement => (
  <>
    {panelReady ? (
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ChatPanel key={threadId} threadId={threadId} />
        {agent.messages.length === 0 && (
          <EmptyChatHints
            projectId={projectId}
            threadId={threadId}
            onSelectThread={onSelectThread}
          />
        )}
      </Box>
    ) : (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
        <CircularProgress size={20} />
      </Box>
    )}
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      columnGap={1}
      px={1}
    >
      <ApprovalModeSelect autoApprove={autoApprove} onChange={onAutoApproveChange} />
      <Typography variant="caption" color="text.secondary">
        {CHAT_LABELS.chatDisclaimerText}
      </Typography>
    </Stack>
  </>
)

export default ChatBody
